import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  Sun,
  Moon,
  ArrowUpRight,
  ShieldCheck,
  Scale,
  UserCheck,
  Workflow,
  HeartHandshake,
  Activity,
  Users,
  FileText,
  BookOpen,
  HelpCircle,
  Calendar,
  Clock,
  Sparkles
} from 'lucide-react';
import logo from '../assets/logo.png';
import { getFeaturedEvent } from '../data/eventsData';

export default function HeaderNav({ onScheduleClick, isDarkMode, toggleTheme, onSelectService }) {
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const megaMenuRef = useRef(null);
  const triggerRef = useRef(null);

  // Close mega menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMegaMenuOpen &&
        megaMenuRef.current &&
        !megaMenuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsMegaMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isMegaMenuOpen) {
        setIsMegaMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMegaMenuOpen]);

  const handleLinkClick = (e, targetId) => {
    setIsMegaMenuOpen(false);
    if (targetId === 'schedule-consultation' && onScheduleClick) {
      e.preventDefault();
      onScheduleClick();
    }
  };

  const handleProductClick = (e, serviceId) => {
    if (e) e.preventDefault();
    setIsMegaMenuOpen(false);
    if (onSelectService) {
      onSelectService(serviceId);
    } else {
      const el = document.getElementById('services');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const featuredEvent = getFeaturedEvent();

  const handleEventNavigation = (e) => {
    setIsMegaMenuOpen(false);
    if (!featuredEvent?.targetUrl) {
      e.preventDefault();
      if (onScheduleClick) {
        onScheduleClick();
      } else {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // 4 Columns Mega Menu Data
  const megaMenuData = {
    products: [
      {
        serviceId: 'service-1',
        title: 'Frontline Conflict Resolution',
        desc: 'De escalation & operational peace in clinical workflows',
        icon: ShieldCheck
      },
      {
        serviceId: 'service-2',
        title: 'Executive & Physician Mediation',
        desc: 'Statutory confidential ADR under Section 1157',
        icon: Scale
      },
      {
        serviceId: 'service-3',
        title: 'Civility Culture Protocols',
        desc: 'Psychological safety & clinician remediation',
        icon: HeartHandshake
      },
      {
        serviceId: 'service-4',
        title: '1 on 1 Executive Coaching',
        desc: 'Retaining clinical department chairs & CMOs',
        icon: UserCheck
      },
      {
        serviceId: 'service-5',
        title: 'Strategic Leadership Development',
        desc: 'Enterprise stewardship for new medical directors',
        icon: Sparkles
      },
      {
        serviceId: 'service-6',
        title: 'High Pressure Leadership',
        desc: 'Cognitive load & crisis poise for ER and ICU heads',
        icon: Activity
      },
      {
        serviceId: 'service-7',
        title: 'Prosci® ADKAR® Transformation',
        desc: 'Structured organizational change for health systems',
        icon: Workflow
      },
      {
        serviceId: 'service-8',
        title: 'Hiring Right from the Start',
        desc: 'Behavioral assessment matrix for clinical leaders',
        icon: Users
      }
    ],
    resources: [
      {
        title: 'Executive White Papers',
        desc: 'Peer reviewed research on clinical conflict ROI',
        icon: FileText,
        href: '#white-papers'
      },
      {
        title: 'Healthcare Leadership Insights',
        desc: 'Monthly executive analysis, case studies & articles',
        icon: BookOpen,
        href: '#latest-insights'
      },
      {
        title: 'Frequently Asked Questions',
        desc: 'Engagement speed, neutrality protocols & retainers',
        icon: HelpCircle,
        href: '#contact'
      }
    ]
  };

  return (
    <>
      {/* ==================== NOTCH NAVBAR ==================== */}
      <header className="fixed top-0 inset-x-0 z-50 h-16 flex px-0 transition-colors duration-200">
        
        {/* Left Side Bar - Flexible width with hairline outlines */}
        <div className="flex-1 h-10 bg-white/95 dark:bg-[#070b12]/95 backdrop-blur-md z-20 relative min-w-0 transition-colors duration-200">
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
            <line
              x1="0"
              y1="39.5"
              x2="100%"
              y2="39.5"
              stroke="currentColor"
              strokeOpacity={isDarkMode ? 0.18 : 0.08}
              strokeWidth={0.75}
              className="text-gray-900 dark:text-gray-200"
            />
            <line
              x1="0"
              y1="36.5"
              x2="100%"
              y2="36.5"
              stroke="currentColor"
              strokeOpacity={isDarkMode ? 0.12 : 0.04}
              strokeWidth={0.5}
              className="text-gray-900 dark:text-gray-200"
            />
          </svg>
        </div>

        {/* Responsive Notch Container - 3 Slices */}
        <div className="flex h-16 relative z-10 shrink-0 -ml-px w-full max-w-[1360px] mx-auto px-1 sm:px-2">
          
          {/* Left Slice (Corner Curve) */}
          <div className="w-[50px] h-full relative shrink-0">
            {/* Glass Background with exact notch bezier curve */}
            <div
              className="absolute inset-0 bg-white/95 dark:bg-[#070b12]/95 backdrop-blur-md transition-colors duration-200"
              style={{ clipPath: "path('M0 0 H50 V64 C25 64 25 40 0 40 Z')" }}
            />
            {/* Curved Outlines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 50 64">
              <path
                d="M0 39.5 C25 39.5 25 63.5 50 63.5"
                fill="none"
                stroke="currentColor"
                strokeOpacity={isDarkMode ? 0.18 : 0.08}
                strokeWidth={0.75}
                className="text-gray-900 dark:text-gray-200"
              />
              <path
                d="M0 36.5 C25 36.5 25 60.5 50 60.5"
                fill="none"
                stroke="currentColor"
                strokeOpacity={isDarkMode ? 0.12 : 0.04}
                strokeWidth={0.5}
                className="text-gray-900 dark:text-gray-200"
              />
            </svg>
          </div>

          {/* Center Slice (Content Area) */}
          <div className="flex-1 h-full relative min-w-0 -ml-px">
            {/* Background & Bottom Hairline Lines */}
            <div className="absolute inset-0 bg-white/95 dark:bg-[#070b12]/95 backdrop-blur-md transition-colors duration-200">
              <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
                <line
                  x1="0"
                  y1="63.5"
                  x2="100%"
                  y2="63.5"
                  stroke="currentColor"
                  strokeOpacity={isDarkMode ? 0.18 : 0.08}
                  strokeWidth={0.75}
                  className="text-gray-900 dark:text-gray-200"
                />
                <line
                  x1="0"
                  y1="60.5"
                  x2="100%"
                  y2="60.5"
                  stroke="currentColor"
                  strokeOpacity={isDarkMode ? 0.12 : 0.04}
                  strokeWidth={0.5}
                  className="text-gray-900 dark:text-gray-200"
                />
              </svg>
            </div>

            {/* Content Layer */}
            <div className="relative w-full h-full flex items-end justify-between pb-2.5 px-2 sm:px-4 md:px-6 gap-2 sm:gap-4">
              
              {/* Left Notch Section: Nav 6 Trigger Toggle + Desktop Direct Links */}
              <div className="flex items-center gap-2 sm:gap-4 mb-0.5 shrink-0">
                {/* Nav 6 Hamburger/Close Mega-Menu Trigger Button */}
                <button
                  ref={triggerRef}
                  type="button"
                  onClick={() => setIsMegaMenuOpen(prev => !prev)}
                  aria-expanded={isMegaMenuOpen}
                  aria-label="Toggle navigation menu and resources"
                  className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 border ${
                    isMegaMenuOpen
                      ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20'
                      : 'bg-gray-100 dark:bg-clinical-slate-800 text-gray-700 dark:text-gray-200 hover:text-primary dark:hover:text-primary-fixed border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <motion.div
                    animate={{ rotate: isMegaMenuOpen ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {isMegaMenuOpen ? (
                      <X className="w-4 h-4 text-white" />
                    ) : (
                      <Menu className="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-primary" />
                    )}
                  </motion.div>
                  <span className="hidden sm:inline font-medium text-xs">
                    {isMegaMenuOpen ? 'Close' : 'Menu'}
                  </span>
                </button>

                {/* Desktop Primary Nav Links */}
                <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
                  <a
                    href="#about"
                    className="text-xs xl:text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-fixed transition-colors whitespace-nowrap"
                  >
                    About Us
                  </a>
                  <a
                    href="#strategic-tiers"
                    className="text-xs xl:text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-fixed transition-colors whitespace-nowrap"
                  >
                    Strategic Tiers
                  </a>
                  <a
                    href="#services"
                    className="text-xs xl:text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-fixed transition-colors whitespace-nowrap"
                  >
                    Portfolio
                  </a>
                </nav>
              </div>

              {/* Center Logo with enlarged prominence */}
              <div className="flex justify-center shrink-0 mx-1 sm:mx-2 mb-0.5">
                <a
                  href="#home"
                  onClick={(e) => handleLinkClick(e, 'home')}
                  className="flex items-center gap-2 sm:gap-2.5 group focus:outline-none"
                >
                  <img
                    src={logo}
                    alt="NFBG Scorpio"
                    className="h-9 sm:h-11 w-auto object-contain transition-transform group-hover:scale-105 drop-shadow-xs"
                  />
                  <div className="flex flex-col text-left leading-none">
                    <span className="text-sm sm:text-base font-black tracking-tight text-gray-900 dark:text-white">
                      NFBG <span className="text-primary font-bold">SCORPIO</span>
                    </span>
                    <span className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold hidden sm:block mt-0.5">
                      Healthcare Advisory
                    </span>
                  </div>
                </a>
              </div>

              {/* Right Notch Section: Secondary Links + Theme Toggle + CTA Button */}
              <div className="flex items-center gap-2 sm:gap-3 mb-0.5 shrink-0">
                <nav className="hidden xl:flex items-center gap-4">
                  <a
                    href="#white-papers"
                    className="text-xs xl:text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-fixed transition-colors whitespace-nowrap"
                  >
                    White Papers
                  </a>
                  <a
                    href="#latest-insights"
                    className="text-xs xl:text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-fixed transition-colors whitespace-nowrap"
                  >
                    Insights
                  </a>
                </nav>

                {/* Schedule Consultation CTA Button */}
                <button
                  type="button"
                  onClick={onScheduleClick}
                  className="px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-semibold text-white bg-primary hover:bg-primary-container rounded-full shadow-xs hover:shadow-md transition-all duration-200 whitespace-nowrap flex items-center gap-1.5"
                >
                  <span>Schedule</span>
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-90 hidden sm:inline-block" />
                </button>
              </div>

            </div>
          </div>

          {/* Right Slice (Corner Curve) */}
          <div className="w-[50px] h-full relative shrink-0 -ml-px">
            {/* Glass Background with exact notch bezier curve */}
            <div
              className="absolute inset-0 bg-white/95 dark:bg-[#070b12]/95 backdrop-blur-md transition-colors duration-200"
              style={{ clipPath: "path('M0 0 H50 V40 C25 40 25 64 0 64 Z')" }}
            />
            {/* Curved Outlines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 50 64">
              <path
                d="M0 63.5 C25 63.5 25 39.5 50 39.5"
                fill="none"
                stroke="currentColor"
                strokeOpacity={isDarkMode ? 0.18 : 0.08}
                strokeWidth={0.75}
                className="text-gray-900 dark:text-gray-200"
              />
              <path
                d="M0 60.5 C25 60.5 25 36.5 50 36.5"
                fill="none"
                stroke="currentColor"
                strokeOpacity={isDarkMode ? 0.12 : 0.04}
                strokeWidth={0.5}
                className="text-gray-900 dark:text-gray-200"
              />
            </svg>
          </div>

        </div>

        {/* Right Side Bar - Flexible width with hairline outlines */}
        <div className="flex-1 h-10 bg-white/95 dark:bg-[#070b12]/95 backdrop-blur-md z-20 relative min-w-0 -ml-px transition-colors duration-200">
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
            <line
              x1="0"
              y1="39.5"
              x2="100%"
              y2="39.5"
              stroke="currentColor"
              strokeOpacity={isDarkMode ? 0.18 : 0.08}
              strokeWidth={0.75}
              className="text-gray-900 dark:text-gray-200"
            />
            <line
              x1="0"
              y1="36.5"
              x2="100%"
              y2="36.5"
              stroke="currentColor"
              strokeOpacity={isDarkMode ? 0.12 : 0.04}
              strokeWidth={0.5}
              className="text-gray-900 dark:text-gray-200"
            />
          </svg>
        </div>

      </header>

      {/* ==================== NAVIGATION 6 MEGA-MENU POPOVER ==================== */}
      <AnimatePresence>
        {isMegaMenuOpen && (
          <>
            {/* Backdrop Dimmer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsMegaMenuOpen(false)}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-xs"
              aria-hidden="true"
            />

            {/* Anchored Expandable Mega-Menu Popover */}
            <div className="fixed top-16 inset-x-0 z-40 px-3 sm:px-6 lg:px-8 py-3 pointer-events-none">
              <motion.div
                ref={megaMenuRef}
                initial={{ opacity: 0, y: -10, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.985 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="pointer-events-auto max-w-7xl mx-auto bg-white/98 dark:bg-[#0b111e]/98 backdrop-blur-2xl rounded-3xl border border-gray-200/90 dark:border-gray-800 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.2),0_0_1px_1px_rgba(0,0,0,0.06)] overflow-hidden max-h-[85vh] overflow-y-auto"
              >
                {/* Mega Menu Grid: 3 Columns (Products, Resources, Featured Webinar) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-8 p-6 sm:p-8 lg:p-10">
                  
                  {/* Column 1: Products & Practice (Span 6 on lg, 2 on md) */}
                  <div className="md:col-span-2 lg:col-span-6 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-primary">
                          Offerings
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Products &amp; Practice
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
                        {megaMenuData.products.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <button
                              key={item.serviceId}
                              type="button"
                              onClick={(e) => handleProductClick(e, item.serviceId)}
                              className="w-full text-left group flex items-start gap-2.5 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-clinical-slate-800/60 transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 text-primary flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 group-hover:bg-primary group-hover:text-white transition-all">
                                <ItemIcon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary-fixed transition-colors truncate">
                                  {item.title}
                                </div>
                                <p className="text-[11px] text-white dark:text-gray-400 line-clamp-1 mt-0.5">
                                  {item.desc}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="pt-3 mt-4 border-t border-gray-100 dark:border-gray-800">
                      <a
                        href="#services"
                        onClick={(e) => handleLinkClick(e, 'services')}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-container transition-colors"
                      >
                        <span>View All Practice Offerings</span>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>

                  {/* Column 2: Knowledge & Resources (Span 3 on lg, 1 on md) */}
                  <div className="md:col-span-1 lg:col-span-3 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-secondary">
                          Knowledge
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Resources
                      </h3>
                      <div className="space-y-3">
                        {megaMenuData.resources.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <a
                              key={item.title}
                              href={item.href}
                              onClick={(e) => handleLinkClick(e, item.href.replace('#', ''))}
                              className="group flex items-start gap-3 p-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-clinical-slate-800/60 transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-secondary/10 dark:bg-secondary/20 text-secondary flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-105 group-hover:bg-secondary group-hover:text-white transition-all">
                                <ItemIcon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white group-hover:text-secondary transition-colors">
                                  {item.title}
                                </div>
                                <p className="text-[11px] text-white dark:text-gray-400 line-clamp-2 mt-0.5">
                                  {item.desc}
                                </p>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>

                    <div className="pt-3 mt-4 border-t border-gray-100 dark:border-gray-800">
                      <a
                        href="#white-papers"
                        onClick={(e) => handleLinkClick(e, 'white-papers')}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary hover:underline transition-colors"
                      >
                        <span>Access Executive White Papers</span>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>

                  {/* Column 3: Featured LinkedIn Event Highlight Card (Span 3 on lg, 1 on md) */}
                  <div className="md:col-span-1 lg:col-span-3 flex flex-col">
                    <div className="h-full rounded-2xl p-5 bg-gradient-to-br from-primary/10 via-primary/5 to-accent-teal/10 dark:from-[#0f172a] dark:via-[#111827] dark:to-[#0f172a] border border-primary/20 dark:border-gray-800 flex flex-col justify-between relative overflow-hidden shadow-sm">
                      {/* Ambient Accent Glow */}
                      <div className="absolute -top-12 -right-12 w-28 h-28 bg-primary/20 dark:bg-primary/10 rounded-full blur-2xl pointer-events-none" />

                      <div className="relative z-10">
                        {/* Event Live Status Badge */}
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white dark:bg-gray-800/90 text-primary dark:text-cyan-300 border border-primary/20 dark:border-gray-700 shadow-xs mb-3">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              featuredEvent?.isUpcoming ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                            }`}
                          />
                          <span>{featuredEvent?.badgeText || 'Executive Briefing'}</span>
                        </div>

                        {/* Title */}
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white leading-snug mb-2">
                          {featuredEvent?.title}
                        </h4>

                        {/* Description */}
                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                          {featuredEvent?.description}
                        </p>

                        {/* Metadata Box - High-Contrast Dark & Light Mode */}
                        <div className="space-y-2 text-[11px] bg-white/95 dark:bg-[#070b12]/95 backdrop-blur-md p-3.5 rounded-xl border border-gray-200/90 dark:border-gray-700/80 shadow-xs">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-primary dark:text-cyan-400 shrink-0" />
                            <span className="font-semibold text-gray-900 dark:text-white">{featuredEvent?.displayDate}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-primary dark:text-cyan-400 shrink-0" />
                            <span className="font-medium text-gray-800 dark:text-gray-200">{featuredEvent?.time}</span>
                          </div>
                          <div className="flex items-center gap-2 pt-1.5 border-t border-gray-200/70 dark:border-gray-700/80">
                            <Sparkles className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
                            <span className="font-semibold text-[10px] text-gray-700 dark:text-gray-300">{featuredEvent?.host}</span>
                          </div>
                        </div>
                      </div>

                      {/* Register Action CTA */}
                      <div className="mt-5 relative z-10">
                        <a
                          href={featuredEvent?.targetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleEventNavigation}
                          className="w-full py-2.5 px-4 rounded-xl bg-primary hover:bg-primary-container text-white font-semibold text-xs shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 group cursor-pointer"
                        >
                          <span>{featuredEvent?.ctaText || 'View on LinkedIn'}</span>
                          <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </a>
                        <p className="text-center text-[10px] text-gray-600 dark:text-gray-400 mt-2">
                          {featuredEvent?.tagline}
                        </p>
                      </div>

                    </div>
                  </div>

                </div>

                {/* Bottom Bar inside Mega-Menu */}
                <div className="bg-gray-100 dark:bg-[#070b12] px-6 sm:px-8 py-3.5 border-t border-gray-200 dark:border-gray-800 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-700 dark:text-gray-300">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <span className="font-bold text-gray-900 dark:text-white">
                      NFBG Scorpio Healthcare Practice
                    </span>
                    <span className="hidden sm:inline text-gray-400 dark:text-gray-600">•</span>
                    <span className="hidden sm:inline text-gray-700 dark:text-gray-300 font-medium">
                      Evidence Code § 1157 Statutory Privilege
                    </span>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Manual Theme Toggle Button Inside Menu */}
                    <button
                      type="button"
                      onClick={toggleTheme}
                      aria-label="Toggle light and dark mode"
                      title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors border border-gray-300 dark:border-gray-700 text-xs font-semibold shadow-xs cursor-pointer active:scale-95"
                    >
                      {isDarkMode ? (
                        <>
                          <Sun className="w-3.5 h-3.5 text-amber-400" />
                          <span>Light Mode</span>
                        </>
                      ) : (
                        <>
                          <Moon className="w-3.5 h-3.5 text-gray-600" />
                          <span>Dark Mode</span>
                        </>
                      )}
                    </button>

                    <a
                      href="#contact"
                      onClick={(e) => handleLinkClick(e, 'contact')}
                      className="font-bold text-primary dark:text-primary-fixed hover:underline flex items-center gap-1"
                    >
                      Emergency Retainer Inquiries
                    </a>
                  </div>
                </div>

              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
